import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { OrderListItemDto, ReleaseOrdersResult, ShopDto } from '@thebb/shared';
import { get, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, PageTitle, Spinner, fmtTime } from '../components/ui';

/** What "Frigiv til pluk" can take from the list: as on the order page; gift orders go in batches. */
const releasable = (o: OrderListItemDto) => ['new', 'released', 'partially_shipped'].includes(o.status) && o.type !== 'gift';

export const navisionLabel: Record<string, string> = { open: 'Åben', released: 'Frigivet', shipped: 'Leveret', invoiced: 'Faktureret', archived: 'Arkiveret' };
export const navisionTone = (s: string) => (s === 'invoiced' ? 'green' : s === 'shipped' ? 'blue' : s === 'archived' ? 'zinc' : 'amber') as 'green' | 'blue' | 'zinc' | 'amber';

export const statusLabel: Record<string, string> = {
  new: 'Ny',
  released: 'Frigivet',
  in_progress: 'I gang',
  partially_shipped: 'Delvist sendt',
  shipped: 'Sendt',
  completed: 'Afsluttet',
  on_hold: 'På hold',
  needs_check: 'Skal tjekkes',
  cancelled: 'Annulleret',
  awaiting_approval: 'Til godkendelse',
  awaiting_payment: 'Venter på betaling',
};
export const statusTone = (s: string): 'zinc' | 'green' | 'red' | 'amber' | 'blue' =>
  s === 'completed' || s === 'shipped' ? 'green' : s === 'cancelled' ? 'zinc' : s === 'needs_check' || s === 'on_hold' ? 'red' : s === 'new' ? 'blue' : 'amber';
export const deliveryLabel: Record<string, string> = { pickup: 'Afhentning', parcel_shop: 'Pakkeshop', business: 'Firmalevering', home: 'Privat' };
export const paymentLabel: Record<string, string> = { bambora: 'Kort', dibs: 'Kort (DIBS)', invoice: 'Faktura', gift: 'Gave', points: 'Point', unknown: '–' };
export const money = (minor: number, currency: string | null) => (currency ? `${(minor / 100).toLocaleString('da-DK', { minimumFractionDigits: 2 })} ${currency}` : '–');

type Paged<T> = { items: T[]; total: number; page: number; perPage: number };

export function OrdersPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [shopId, setShopId] = useState('');
  const [needsCheck, setNeedsCheck] = useState(false);
  const [navision, setNavision] = useState('');
  const [demo, setDemo] = useState('hide');
  const [page, setPage] = useState(1);
  const params = new URLSearchParams({ page: String(page), perPage: '50' });
  if (q) params.set('q', q);
  if (status) params.set('status', status);
  if (shopId) params.set('shopId', shopId);
  if (needsCheck) params.set('needsCheck', 'true');
  if (navision) params.set('navision', navision);
  if (demo !== 'hide') params.set('demo', demo);
  const orders = useQuery({ queryKey: ['orders', params.toString()], queryFn: () => get<Paged<OrderListItemDto>>(`/orders?${params}`), refetchInterval: 30_000 });
  const shops = useQuery({ queryKey: ['shops'], queryFn: () => get<ShopDto[]>('/shops') });
  const pages = orders.data ? Math.max(1, Math.ceil(orders.data.total / orders.data.perPage)) : 1;
  const qc = useQueryClient();
  const { can } = useAuth();
  const canRelease = can('orders.release');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<{ res: ReleaseOrdersResult; numbers: Map<string, number> } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const rows = orders.data?.items ?? [];
  const pickable = rows.filter(releasable);
  const release = useMutation({
    mutationFn: (ids: string[]) => post<ReleaseOrdersResult>('/orders/release', { orderIds: ids }),
    onSuccess: (res) => {
      setResult({ res, numbers: new Map(rows.map((o) => [o.id, o.number])) });
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: setError,
  });
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOnPage = pickable.length > 0 && pickable.every((o) => selected.has(o.id));

  return (
    <div>
      <PageTitle
        title="Ordrer"
        actions={
          <>
            <input className="input w-56" placeholder="Søg nr., navn, e-mail, SKU" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
            <select className="input w-40" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">Alle statusser</option>
              {Object.entries(statusLabel).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select className="input w-52" value={shopId} onChange={(e) => { setShopId(e.target.value); setPage(1); }}>
              <option value="">Alle shops</option>
              {shops.data?.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <select className="input w-36" value={demo} onChange={(e) => { setDemo(e.target.value); setPage(1); }} title="Ordrer fra demoshops">
              <option value="hide">Uden demo</option>
              <option value="include">Med demo</option>
              <option value="only">Kun demo</option>
            </select>
            <label className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={needsCheck} onChange={(e) => { setNeedsCheck(e.target.checked); setPage(1); }} /> Skal tjekkes
            </label>
          </>
        }
      />
      <ErrorBox error={error} />
      {canRelease && selected.size > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
          <span>{selected.size} valgt</span>
          <button className="btn btn-primary" disabled={release.isPending} onClick={() => release.mutate([...selected])}>{release.isPending ? 'Frigiver…' : `Frigiv ${selected.size} til pluk`}</button>
          <button className="btn" onClick={() => setSelected(new Set())}>Fravælg</button>
        </div>
      )}
      {result && (
        <div className="mb-3 space-y-1 rounded-md border border-line bg-white p-3 text-sm">
          <div className="flex items-center justify-between">
            <span>
              {result.res.results.filter((x) => x.ok).length} frigivet til pluk
              {result.res.results.some((x) => !x.ok) && `, ${result.res.results.filter((x) => !x.ok).length} kunne ikke`}
            </span>
            <button className="btn" onClick={() => setResult(null)}>Luk</button>
          </div>
          {result.res.results.filter((x) => !x.ok || x.linesBackordered > 0).map((x) => (
            <div key={x.orderId} className={x.ok ? 'text-amber-700' : 'text-red-700'}>
              Ordre {result.numbers.get(x.orderId) ?? x.orderId}: {x.ok ? `${x.fulfilmentNumber}, ${x.linesBackordered} linje(r) i restordre` : x.error}
            </div>
          ))}
        </div>
      )}
      {orders.isLoading ? (
        <Spinner />
      ) : (
        <>
          <table className="table card p-0">
            <thead>
              <tr>
                {canRelease && (
                  <th className="w-8">
                    <input type="checkbox" aria-label="Vælg alle der kan frigives" disabled={!pickable.length} checked={allOnPage} onChange={() => setSelected(allOnPage ? new Set() : new Set(pickable.map((o) => o.id)))} />
                  </th>
                )}
                <th>Nr.</th>
                <th>Shop</th>
                <th>Kunde</th>
                <th>Type</th>
                <th>Status</th>
                <th>Navision</th>
                <th>Betaling</th>
                <th>Levering</th>
                <th className="text-right">Total</th>
                <th>Dato</th>
                {canRelease && <th />}
              </tr>
            </thead>
            <tbody>
              {orders.data?.items.map((o) => (
                <tr key={o.id}>
                  {canRelease && <td>{releasable(o) && <input type="checkbox" aria-label={`Vælg ordre ${o.number}`} checked={selected.has(o.id)} onChange={() => toggle(o.id)} />}</td>}
                  <td>
                    <Link to="/orders/$id" params={{ id: o.id }} className="font-medium text-blue-700 hover:underline">
                      {o.number}
                    </Link>
                    {o.demo && <Badge tone="blue">DEMO</Badge>}
                  </td>
                  <td>{o.shopName}</td>
                  <td>{o.customerName ?? '–'}</td>
                  <td>{o.type === 'gift' ? 'Gave' : o.type === 'points' ? 'Point' : 'Ordre'}</td>
                  <td>
                    <Badge tone={statusTone(o.status)}>{statusLabel[o.status] ?? o.status}</Badge>
                    {o.needsCheckReason && <div className="text-xs text-red-700">{o.needsCheckReason}</div>}
                  </td>
                  <td>{o.navision ? <Badge tone={navisionTone(o.navision.state)}>{navisionLabel[o.navision.state]}</Badge> : <span className="text-muted">–</span>}</td>
                  <td>{paymentLabel[o.paymentProvider] ?? o.paymentProvider}</td>
                  <td>{deliveryLabel[o.deliveryMethod] ?? o.deliveryMethod}</td>
                  <td className="text-right tabular-nums">{money(o.totalMinor, o.currency)}</td>
                  <td className="whitespace-nowrap">{o.placedAt?.slice(0, 10) ?? fmtTime(o.importedAt)}</td>
                  {canRelease && (
                    <td className="text-right">
                      {releasable(o) && <button className="btn whitespace-nowrap py-1 text-xs" disabled={release.isPending} onClick={() => release.mutate([o.id])}>Frigiv til pluk</button>}
                    </td>
                  )}
                </tr>
              ))}
              {orders.data?.items.length === 0 && (
                <tr>
                  <td colSpan={canRelease ? 12 : 10} className="text-zinc-500">Ingen ordrer matcher.</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="mt-3 flex items-center gap-2 text-sm">
            <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>Forrige</button>
            <span>Side {page} af {pages} · {orders.data?.total} ordrer</span>
            <button className="btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>Næste</button>
          </div>
        </>
      )}
    </div>
  );
}
